import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'hooks';

import qualityHeaders from 'static/assets/images/common/qualityHeader.svg';
import SupportIcon from '@mui/icons-material/Support';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { selectUserData } from 'store/user/userSelector';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import LinearScaleOutlinedIcon from '@mui/icons-material/LinearScaleOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft';
import OperatorModal from '@/operator/components/modal/OperatorModal';
import NavigationItem from './NavigationItem';
import NavigationLink from './NavigationLink';
import { appRoutes } from '@/routes/constants';
import useAuthLogout from 'hooks/useAuthLogout';
import AppVersion from 'Components/Common/AppVersion';
import { FirestoreUserRole } from 'generated/graphql';

interface SideDrawerProp {
    drawerWidth: string;
}

export enum SidebarItem {
    HOME,
    ANALYTICS,
    TEAM,
    SCORECARDS,
    CALIBRATIONS_HOME,
    SETTINGS,
    QUEUE,
    OPERATOR_ADMIN_CONNECTIONS,
    OPERATOR_USERS,
}

interface NavigationConfigItem {
    text: string;
    icon: React.ReactNode;
    to: string;
    sidebarItem: SidebarItem;
    adminOnly?: boolean;
}

const navigationConfig: NavigationConfigItem[] = [
    {
        text: 'Home',
        icon: <HomeOutlinedIcon />,
        to: appRoutes.HOME,
        sidebarItem: SidebarItem.HOME,
    },
    {
        text: 'Analytics',
        icon: <ManageSearchOutlinedIcon />,
        to: appRoutes.ANALYTICS,
        sidebarItem: SidebarItem.ANALYTICS,
    },
    {
        text: 'Queue',
        icon: <FormatAlignLeft />,
        to: appRoutes.QUEUE,
        sidebarItem: SidebarItem.QUEUE,
        adminOnly: true,
    },
    {
        text: 'Scorecards',
        icon: <StyleOutlinedIcon />,
        to: appRoutes.SCORECARDS,
        sidebarItem: SidebarItem.SCORECARDS,
        adminOnly: true,
    },
    {
        text: 'Users',
        icon: <Groups2OutlinedIcon />,
        to: appRoutes.OPERATOR_USERS,
        sidebarItem: SidebarItem.OPERATOR_USERS,
        adminOnly: true,
    },
    {
        text: 'Teams',
        icon: <PeopleAltOutlinedIcon />,
        to: appRoutes.TEAM,
        sidebarItem: SidebarItem.TEAM,
    },
    {
        text: 'Calibrations',
        icon: <ExploreOutlinedIcon />,
        to: appRoutes.CALIBRATIONS_HOME,
        sidebarItem: SidebarItem.CALIBRATIONS_HOME,
    },
    {
        text: 'Settings',
        icon: <SettingsOutlined />,
        to: appRoutes.SETTINGS,
        sidebarItem: SidebarItem.SETTINGS,
    },
    {
        text: 'Connections',
        icon: <LinearScaleOutlinedIcon />,
        to: appRoutes.OPERATOR_ADMIN_CONNECTIONS,
        sidebarItem: SidebarItem.OPERATOR_ADMIN_CONNECTIONS,
        adminOnly: true,
    },
];

const SideDrawer: React.FC<SideDrawerProp> = ({ drawerWidth }) => {
    const { roles: loggedUserRoles } = useAppSelector(selectUserData) || {};
    const { pathname } = useLocation();
    const logout = useAuthLogout();

    const [selectedRoute, setSelectedRoute] = useState<SidebarItem>();
    const [open, setOpen] = useState(false);

    const routeToSidebarItemMap: { [key: string]: SidebarItem } = {
        [appRoutes.HOME]: SidebarItem.HOME,
        [appRoutes.ANALYTICS]: SidebarItem.ANALYTICS,
        [appRoutes.TEAM]: SidebarItem.TEAM,
        [appRoutes.SCORECARDS]: SidebarItem.SCORECARDS,
        [appRoutes.CALIBRATIONS_HOME]: SidebarItem.CALIBRATIONS_HOME,
        [appRoutes.SETTINGS]: SidebarItem.SETTINGS,
        [appRoutes.QUEUE]: SidebarItem.QUEUE,
        [appRoutes.OPERATOR_ADMIN_CONNECTIONS]:
            SidebarItem.OPERATOR_ADMIN_CONNECTIONS,
        [appRoutes.OPERATOR_USERS]: SidebarItem.OPERATOR_USERS,
    };

    useEffect(() => {
        setSelectedRoute(routeToSidebarItemMap[pathname]);
    }, [pathname]);

    const handleModalOpen = () => {
        setOpen(true);
    };

    const handleRouteSelect = (route: string) => {
        setSelectedRoute(routeToSidebarItemMap[route]);
    };

    const handleMailTo = () => {
        window.location.href = 'mailto:support@launchoperator.com';
        setOpen(false);
    };

    return (
        <>
            <OperatorModal
                open={open}
                setOpen={setOpen}
                Icon={SupportIcon}
                headline="Need some help?"
                description="Feeling a little lost? Things not working how you think they
        should be? You can drop us an email. Our support hours are Monday
        - Friday 9:00am-6:00pm EST."
                okayButtonLabel="support@launchoperator.com"
                cancelButtonLabel="I’m good, but nice to know you’re here"
                customColor="#4C17AD"
                onClick={handleMailTo}
            />
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                anchor="left"
            >
                <Box
                    paddingLeft={3}
                    paddingRight={1}
                    paddingTop={2}
                    height={({ spacing }) => spacing(8)}
                    alignItems="center"
                    display="flex"
                >
                    <Box
                        component="img"
                        src={qualityHeaders}
                        height={({ spacing }) => spacing(3.5)}
                    />
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    justifyContent="space-between"
                    paddingTop={3}
                    paddingBottom={5}
                >
                    <Box padding={1} component="nav">
                        {navigationConfig.map(
                            ({ text, icon, to, sidebarItem, adminOnly }) => {
                                if (
                                    adminOnly &&
                                    !loggedUserRoles.includes(
                                        FirestoreUserRole.OrganizationAdmin,
                                    )
                                ) {
                                    return null;
                                }

                                return (
                                    <NavigationItem
                                        key={to}
                                        selected={selectedRoute === sidebarItem}
                                        onClick={() => handleRouteSelect(to)}
                                        to={to}
                                        icon={icon}
                                        text={text}
                                    />
                                );
                            },
                        )}

                        <NavigationLink
                            href={process.env.QUALITY_EXTENSION_LINK}
                            icon={<OpenInNewOutlinedIcon />}
                            text="Chrome Extension"
                        />
                    </Box>
                    <Box padding={1}>
                        <ListItemButton onClick={handleModalOpen}>
                            <ListItemIcon>
                                <SupportIcon />
                            </ListItemIcon>
                            <ListItemText>Support</ListItemText>
                        </ListItemButton>
                        <ListItemButton onClick={logout}>
                            <ListItemIcon>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText>Log Out</ListItemText>
                        </ListItemButton>
                        <AppVersion />
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default SideDrawer;
